import React, { useEffect, useMemo, useCallback, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Button } from "@material-ui/core";
import { AiOutlineArrowRight } from "react-icons/ai";
import { getAllOrdersOfUser } from "../../redux/actions/order";
import { useNotifications } from "../../hooks/useNotifications";
import { useMarkNotificationsReadOnPage } from "../../hooks/useMarkNotificationsReadOnPage";
import { ORDER_NOTIFICATION_TYPES, BUYER_REFUND_UPDATE_TYPES } from "../../utils/notificationTypes";
import {
  getNotificationRelatedOrderId,
  isBuyerFacingOrderNotification,
} from "../../utils/orderNotificationMatch";
import {
  getOrderListStatusLabel,
  isOrderListStatusResolvedNoRefund,
  isOrderListStatusSuccess,
} from "../../utils/orderListStatus";
import { SimpleLoader } from "../Layout/Loader";
import DataGridWrapper from "./DataGridWrapper";
import { primeMobileDashboardContentScroll } from "../../utils/navigateWithMobileDashboardScroll";
import OrderUpdatesPanel from "./OrderUpdatesPanel";

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "£0.00";
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
  }).format(Number(value));
};

/** Single-line summary of cart lines for the buying orders table */
const formatCartSummary = (cart) => {
  if (!Array.isArray(cart) || cart.length === 0) return "—";
  return cart
    .map((line) => {
      const name = (line.name || "Item").trim() || "Item";
      const qty = Number(line.qty) || 1;
      return `${name} ×${qty}`;
    })
    .join(", ");
};

const MAX_GRID_ROWS = 200;

const ORDER_LIST_TYPES = new Set([...ORDER_NOTIFICATION_TYPES, ...BUYER_REFUND_UPDATE_TYPES]);

const AllOrders = ({ cardClass }) => {
  const { user } = useSelector((state) => state.user);
  const { orders } = useSelector((state) => state.order);
  const dispatch = useDispatch();
  const { notifications } = useNotifications();
  useMarkNotificationsReadOnPage("order_list");
  const userId = user?._id;
  const [userFetchSent, setUserFetchSent] = useState(false);
  const [userOrdersLoaded, setUserOrdersLoaded] = useState(false);
  const [gridReady, setGridReady] = useState(false);
  const prevUserIdRef = useRef(null);
  const isFetchingRef = useRef(false);

  const orderUnreadMap = useMemo(() => {
    const m = new Map();
    if (!Array.isArray(notifications)) return m;
    notifications.forEach((n) => {
      if (n.read || !ORDER_LIST_TYPES.has(n.type)) return;
      if (!isBuyerFacingOrderNotification(n)) return;
      const id = getNotificationRelatedOrderId(n);
      if (!id) return;
      const prev = m.get(id) || { count: 0, title: "" };
      m.set(id, {
        count: prev.count + 1,
        title: prev.title || n.title || n.message || "Update",
      });
    });
    return m;
  }, [notifications]);

  useEffect(() => {
    const id = requestAnimationFrame(() => setGridReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    const currentUserId = userId;
    if (!currentUserId || currentUserId === prevUserIdRef.current || isFetchingRef.current) {
      return;
    }
    prevUserIdRef.current = currentUserId;
    isFetchingRef.current = true;
    setUserFetchSent(true);
    setUserOrdersLoaded(false);
    dispatch(getAllOrdersOfUser(currentUserId)).finally(() => {
      isFetchingRef.current = false;
      setUserOrdersLoaded(true);
    });
  }, [dispatch, userId]);

  const columns = useMemo(
    () => [
      {
        field: "orderIdDisplay",
        headerName: "Order ID",
        width: 200,
        minWidth: 180,
        maxWidth: 260,
        sortable: false,
        renderCell: (params) => {
          const rowId = params.id?.toString?.() || params.row?.id?.toString?.() || "";
          const fromMap = orderUnreadMap.get(rowId);
          const unreadCount =
            Number(fromMap?.count) || Number(params.row?.unreadCount) || 0;
          const unreadLabel =
            fromMap?.title || params.row?.unreadLabel || "New status update";
          const hasUnread = unreadCount > 0;
          const display =
            params.row?.orderIdDisplay ?? params.value ?? params.getValue?.(params.id, "orderIdDisplay");
          return (
            <div
              className={`flex w-full min-w-0 items-center gap-2 ${
                hasUnread ? "rounded-md bg-red-50 px-1.5 py-1" : ""
              }`}
              title={hasUnread ? unreadLabel : undefined}
            >
              {hasUnread ? (
                <span
                  className="inline-flex shrink-0 items-center rounded bg-red-600 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white"
                  aria-label="New update"
                >
                  New
                </span>
              ) : null}
              <span
                className={`font-semibold ${
                  hasUnread ? "text-red-700" : "text-slate-800"
                }`}
              >
                #{display}
              </span>
              {hasUnread ? (
                <span
                  className="h-2.5 w-2.5 shrink-0 rounded-full bg-red-500"
                  aria-hidden
                />
              ) : null}
            </div>
          );
        },
      },
      {
        field: "productsSummary",
        headerName: "Products",
        minWidth: 200,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const text = params.row.productsSummary || "—";
          return (
            <span className="block max-w-full truncate text-left text-sm text-slate-700" title={text}>
              {text}
            </span>
          );
        },
      },
      {
        field: "status",
        headerName: "Status",
        minWidth: 130,
        flex: 0.6,
        renderCell: (params) => {
          const statusLabel = params.row.statusLabel || params.getValue(params.id, "status");
          const rowId = params.id?.toString?.() || params.row?.id?.toString?.() || "";
          const unreadCount =
            Number(orderUnreadMap.get(rowId)?.count) ||
            Number(params.row?.unreadCount) ||
            0;
          const isGreen = isOrderListStatusSuccess(statusLabel);
          const isResolvedNoRefund = isOrderListStatusResolvedNoRefund(statusLabel);
          return (
            <div className="flex items-center gap-2">
              <span className={isGreen ? "greenColor" : isResolvedNoRefund ? "blueColor" : "redColor"}>
                {statusLabel}
              </span>
              {unreadCount > 0 && (
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-semibold text-white">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
          );
        },
        cellClassName: (params) => {
          const statusLabel = params.row.statusLabel || params.getValue(params.id, "status");
          if (isOrderListStatusSuccess(statusLabel)) return "greenColor";
          if (isOrderListStatusResolvedNoRefund(statusLabel)) return "blueColor";
          return "redColor";
        },
      },
      {
        field: "itemsQty",
        headerName: "Items qty",
        type: "number",
        minWidth: 130,
        flex: 0.5,
      },
      {
        field: "total",
        headerName: "Total",
        type: "number",
        minWidth: 130,
        flex: 0.6,
      },
      {
        field: "actions",
        flex: 0.4,
        minWidth: 120,
        headerName: "",
        sortable: false,
        renderCell: (params) => (
          <Link
            to={`/profile/order/${params.id}`}
            onClick={(e) => {
              e.stopPropagation();
              primeMobileDashboardContentScroll(`/profile/order/${params.id}`);
            }}
          >
            <Button>
              <AiOutlineArrowRight size={18} />
            </Button>
          </Link>
        ),
      },
    ],
    [orderUnreadMap]
  );

  const getRowStatusLabel = (order) => getOrderListStatusLabel(order);

  const rows = useMemo(
    () => {
      if (!gridReady || !orders || !Array.isArray(orders)) return [];
      const list = orders.slice(0, MAX_GRID_ROWS);
      const sorted = [...list].sort((a, b) => {
        const aUnread = orderUnreadMap.get(a._id?.toString())?.count || 0;
        const bUnread = orderUnreadMap.get(b._id?.toString())?.count || 0;
        if (bUnread !== aUnread) return bUnread - aUnread;
        const na = Number(a?.orderNumber) || 0;
        const nb = Number(b?.orderNumber) || 0;
        if (nb !== na) return nb - na;
        const ta = new Date(a?.createdAt || 0).getTime();
        const tb = new Date(b?.createdAt || 0).getTime();
        return tb - ta;
      });
      return sorted.map((item) => {
        const id = item._id?.toString();
        const orderNum = item?.orderNumber;
        const unread = orderUnreadMap.get(id);
        return {
          id: item._id,
          orderIdDisplay: orderNum != null ? String(orderNum) : item?._id?.toString().substring(0, 8) ?? "N/A",
          productsSummary: formatCartSummary(item.cart),
          itemsQty: item.cart?.length ?? 0,
          total: formatCurrency(item.totalPrice || 0),
          status: getRowStatusLabel(item),
          statusLabel: getRowStatusLabel(item),
          unreadCount: unread?.count || 0,
          unreadLabel: unread?.title || "",
        };
      });
    },
    [orders, gridReady, orderUnreadMap]
  );

  const unreadOrderNotifications = useMemo(() => {
    if (!Array.isArray(notifications)) return [];
    return notifications
      .filter((n) => !n.read && ORDER_LIST_TYPES.has(n.type) && isBuyerFacingOrderNotification(n))
      .sort(
        (a, b) =>
          new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime()
      );
  }, [notifications]);

  const handleRowClick = useCallback((params, navigate) => {
    navigate(`/profile/order/${params.id}`);
  }, []);

  const showLoader = !userFetchSent || !userOrdersLoaded;
  if (showLoader) {
    return (
      <section className={cardClass}>
        <SimpleLoader />
      </section>
    );
  }
  if (!gridReady) {
    return (
      <section className={cardClass}>
        <SimpleLoader />
      </section>
    );
  }

  const truncated = Array.isArray(orders) && orders.length > MAX_GRID_ROWS;
  const description = truncated
    ? `Track the progress of all your purchases. Showing first ${MAX_GRID_ROWS} of ${orders.length} orders.`
    : "Track the progress of all your purchases.";

  return (
    <div className="space-y-3">
      <OrderUpdatesPanel notifications={unreadOrderNotifications} />

      <DataGridWrapper
        title="Orders"
        description={description}
        rows={rows}
        columns={columns}
        cardClass={cardClass}
        emptyMessage="No orders yet."
        onRowClick={handleRowClick}
        getRowClassName={(params) =>
          (params.row?.unreadCount || 0) > 0 ? "order-row-has-update" : ""
        }
      />
    </div>
  );
};

export default AllOrders;
